import { useNavigate } from 'react-router'

import { routes } from '@/app/routes'
import {
  useClearDocuments,
  useCountries,
  useEraseAccount,
  useProfile,
  useResetProfile,
  useResidenceTypes,
  useSaveProfile,
  useSetPlan,
  useSubscription,
} from '@/shared/api/queries'
import { formatFullDate } from '@/shared/format'
import { LANGUAGES, useLocalization } from '@/shared/i18n'
import type { Language, TranslationKey } from '@/shared/i18n'
import { useFirstName } from '@/shared/telegram'
import { Alert, Button, Card, Chip, ConfirmAction, Row, Stack, Text, useToast } from '@/shared/ui'
import { clear as clearVault } from '@/shared/vault'

import { ONBOARDING_SURVEY } from '@/features/onboarding/OnboardingWizard'
import { forgetAnswers } from '@/features/survey/draft'

function languageLabel(language: Language): TranslationKey {
  return `language.${language}`
}

function Identity() {
  const { t, language } = useLocalization()
  const firstName = useFirstName()

  const { data: profile } = useProfile()
  const { data: countries } = useCountries()
  const { data: residenceTypes } = useResidenceTypes(profile?.residence_country ?? '')

  const country = countries?.find((candidate) => candidate.code === profile?.residence_country)
  const name = firstName ?? t('onboarding.title')

  return (
    <Stack gap="md">
      <Card>
        <Stack direction="row" gap="md">
          <span className="avatar" aria-hidden="true">
            {name.slice(0, 1)}
          </span>
          <Stack gap="xs">
            <Text as="h1" role="heading-3">
              {name}
            </Text>
            <Text role="caption" tone="accent">
              {t('settings.telegram')}
            </Text>
          </Stack>
        </Stack>
      </Card>

      <div className="grid-2">
        <Card>
          <Text role="eyebrow" tone="muted">
            {t('settings.living')}
          </Text>
          <Stack gap="xs">
            <Text role="title">{country?.name ?? profile?.residence_country ?? '—'}</Text>
            {profile?.arrival_date ? (
              <Text role="caption" tone="muted">
                {t('settings.since', { date: formatFullDate(profile.arrival_date, language) })}
              </Text>
            ) : null}
          </Stack>
        </Card>

        <Card>
          <Text role="eyebrow" tone="muted">
            {t('settings.status')}
          </Text>
          <Text role="title">
            {residenceTypes?.find((option) => option.code === profile?.residence_type)?.name ??
              profile?.residence_type ??
              t('settings.status.unknown')}
          </Text>
        </Card>
      </div>
    </Stack>
  )
}

function LanguagePicker() {
  const { t, language, setLanguage } = useLocalization()
  const { data: profile } = useProfile()
  const save = useSaveProfile(Boolean(profile))
  const toast = useToast()

  function pick(next: Language) {
    setLanguage(next)

    if (profile) {
      save.mutate(
        { ...profile, language: next },
        { onSuccess: () => toast(t('toast.language'), 'success') },
      )
    }
  }

  return (
    <Card>
      <Text as="h2" role="eyebrow" tone="muted">
        {t('settings.language')}
      </Text>
      <Stack gap="sm" direction="row" wrap>
        {LANGUAGES.map((candidate) => (
          <Chip key={candidate} selected={candidate === language} onClick={() => pick(candidate)}>
            {t(languageLabel(candidate))}
          </Chip>
        ))}
      </Stack>
    </Card>
  )
}

function DangerZone() {
  const { t } = useLocalization()
  const erase = useEraseAccount()

  if (erase.isSuccess) {
    return (
      <Text role="caption" tone="muted">
        {t('settings.danger.done')}
      </Text>
    )
  }

  return (
    <Card>
      <Text as="h2" role="eyebrow" tone="muted">
        {t('settings.danger')}
      </Text>
      <ConfirmAction
        word={t('settings.danger.word')}
        label={t('settings.danger.confirm')}
        action={t('settings.danger.action')}
        body={t('settings.danger.body')}
        pending={erase.isPending}
        {...(erase.error ? { error: erase.error.message } : {})}
        onConfirm={() =>
          erase.mutate(undefined, {
            // Erasure is a promise (PRD §11), and the files this device kept
            // are part of what has to go.
            onSuccess: () => void clearVault(),
          })
        }
      />
    </Card>
  )
}

// Workbench is the three switches that make a local run testable: premium
// without paying for it, an empty document shelf to upload the same card into
// again, and a profile reset to walk onboarding a second time. It renders only
// in a development build, and its endpoints exist only when the API runs with
// APP_ENV=development — so shipping it is not a matter of remembering to take
// it out.
/* eslint-disable no-restricted-syntax -- a development-only panel: these strings
   are never shown to a user, and putting them in the translation catalogues
   would ship them to translators along with everything real. */
function Workbench() {
  const navigate = useNavigate()
  const { data: subscription } = useSubscription()
  const plan = useSetPlan()
  const clear = useClearDocuments()
  const reset = useResetProfile()
  const toast = useToast()

  const current = subscription?.plan ?? 'free'
  const failure = plan.error ?? clear.error ?? reset.error

  return (
    <Card>
      <Text as="h2" role="eyebrow" tone="muted">
        Development
      </Text>

      <Stack gap="sm">
        <Text role="caption" tone="muted">
          Plan: {current}
        </Text>

        <Stack direction="row" gap="sm" wrap>
          <Button
            size="sm"
            variant={current === 'premium' ? 'quiet' : 'primary'}
            disabled={plan.isPending}
            onClick={() => plan.mutate(current === 'premium' ? 'free' : 'premium')}
          >
            {current === 'premium' ? 'Back to free' : 'Grant premium'}
          </Button>

          <Button
            size="sm"
            variant="quiet"
            disabled={clear.isPending}
            onClick={() =>
              clear.mutate(undefined, {
                onSuccess: (deleted) => {
                  void clearVault()
                  toast(`Deleted ${deleted} document(s)`, 'success')
                },
              })
            }
          >
            Delete all documents
          </Button>

          <Button
            size="sm"
            variant="quiet"
            disabled={reset.isPending}
            onClick={() =>
              reset.mutate(undefined, {
                onSuccess: () => {
                  // The saved answers of the last run would otherwise reopen the
                  // wizard already filled in, which is not what a reset is for.
                  forgetAnswers(ONBOARDING_SURVEY)
                  void navigate(routes.onboarding)
                },
              })
            }
          >
            Reset profile
          </Button>
        </Stack>

        {failure ? <Alert status="danger" title={failure.message} /> : null}
      </Stack>
    </Card>
  )
}

/* eslint-enable no-restricted-syntax */

export function SettingsPage() {
  const { t } = useLocalization()

  return (
    <Stack gap="md">
      <Identity />

      <Card>
        <Stack as="ul" gap="xs">
          <Row variant="plain" to={routes.subscription} icon="✦" title={t('settings.plan')} />
          <Row variant="plain" to={routes.chat} icon="💬" title={t('settings.chat')} />
          <Row variant="plain" to={routes.onboarding} icon="👤" title={t('onboarding.title')} />
        </Stack>
      </Card>

      <LanguagePicker />

      {import.meta.env.DEV ? <Workbench /> : null}

      <DangerZone />

      <Text role="caption" tone="muted">
        {t('app.name')}
      </Text>
    </Stack>
  )
}
